import { useEffect, useRef } from 'react';
import { MinusIcon } from '@phosphor-icons/react';

interface CheckboxProps {
  checked: boolean;
  label?: string;
  onChange: () => void;
  isInvalid?: boolean;
  disabled?: boolean;
  indeterminate?: boolean;
  className?: string;
  size?: 'lg' | 'md' | 'xl' | '2xl';
  isCircular?: boolean;
  variant?: 'info' | 'success';
}
function Checkbox({
  checked,
  label = '',
  onChange,
  isInvalid = false,
  disabled = false,
  indeterminate = false,
  className = '',
  size = 'md',
  isCircular = false,
  variant = 'info'
}: CheckboxProps) {
  const ref = useRef<HTMLInputElement>(null);
  useEffect(() => {
    if (ref.current) {
      ref.current.indeterminate = indeterminate;
    }
  }, [indeterminate]);

  const getSize = () => {
    switch (size) {
      case '2xl':
        return 'w-8 h-8';
      case 'xl':
        return 'w-6 h-6';
      case 'lg':
        return 'w-5 h-5';
      case 'md':
      default:
        return 'w-4 h-4';
    }
  };
  const getLabelSize = () => {
    switch (size) {
      case 'lg':
        return 'text-base';
      case 'md':
      default:
        return 'text-sm';
    }
  };

  const getSVGSize = () => {
    switch (size) {
      case '2xl':
        return { width: '28', height: '14' };
      case 'xl':
        return { width: '24', height: '10' };
      case 'lg':
        return { width: '12', height: '8' };
      case 'md':
      default:
        return { width: '10', height: '7' };
    }
  };

  type VariantType = 'info' | 'success';

  const variantStyles: Record<VariantType, string> = {
    info: 'checked:bg-blue-500 checked:border-0 indeterminate:bg-blue-500 indeterminate:border-0',
    success: 'checked:bg-[#01B876] checked:border-0 indeterminate:bg-[#01B876] indeterminate:border-0',
  };

  const getCheckboxColor = () => {
    if (disabled) return 'border-grey-600';
    if (isInvalid) return 'checked:bg-red-500 indeterminate:bg-red-500 border-red-500';

    const currentVariant = variantStyles[variant as VariantType] || variantStyles.info;

    return checked ? currentVariant : 'checked:bg-blue-500 checked:border-0 indeterminate:bg-blue-500 indeterminate:border-0';
  };

  return (
    <div className={`flex ${className} ${disabled ? 'opacity-30 pointer-events-none' : ''}`}>
      <label className="whitespace-nowrap flex gap-2 items-center relative cursor-pointer">
        <input
          ref={ref}
          name={`${label}`} type="checkbox" className={`relative peer shrink-0
      appearance-none ${getSize()} ${getCheckboxColor()} border border-divider rounded bg-white cursor-pointer ${isCircular && 'rounded-full'}
      `} checked={checked} onChange={onChange} disabled={disabled} />

        {label && <span className={getLabelSize()}>{label}</span>}

        <span className={`absolute hidden peer-checked:flex items-center justify-center ${getSize()}`}>
          <svg width={getSVGSize().width} height={getSVGSize().height} viewBox="0 0 10 7" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M8.24503 1.59009L3.99503 5.84009L1.74503 3.59009" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        </span>
        <span className={`absolute hidden peer-indeterminate:flex items-center justify-center ${getSize()}`}>
          <MinusIcon className="text-white" />
        </span>
      </label>
    </div>
  );
}

export default Checkbox;
